<template>
   <div class="mb-3 date-range-filter">
      <div class="row g-2">
         <!-- Preset list (SelectField: follows main.js formStyle like every other box) -->
         <SelectField
            ref="presetSelect"
            :col="needsYear ? 'col-8' : 'col-12'"
            label="Date range"
            icon="fa fa-calendar"
            :options="presetOptions"
            option-label="label"
            :reduce="option => option.value"
            :model-value="preset"
            :selectable="option => !option.heading"
            :clearable="false"
            :filter="filterPresets"
            @update:model-value="choosePreset"
         >
               <!-- Headings (Days, Week, Month...) in grey, items normal -->
               <template #option="option">
                  <span v-if="option.heading" class="preset-heading">{{ option.label }}</span>
                  <span v-else>{{ option.label }}</span>
               </template>

               <!-- What shows in the closed box -->
               <template #selected-option>
                  {{ selectedLabel }}
               </template>

               <!-- "Custom…" pinned to the bottom of the open list -->
               <template #list-footer>
                  <li
                     class="preset-custom"
                     :class="{ active: preset === 'custom' }"
                     @mousedown.prevent
                     @click="pickCustom"
                  >
                     <i class="fa fa-calendar-o me-1"></i> Custom…
                  </li>
               </template>
         </SelectField>

         <!-- Year: only for First / Second / Third / Fourth quarter -->
         <SelectField
            v-if="needsYear"
            col="col-4"
            label="Year"
            :options="yearOptions"
            :model-value="year"
            :clearable="false"
            :searchable="false"
            @update:model-value="chooseYear"
         />
      </div>

      <!-- Custom: pick both dates yourself (DateField: themed calendar with the time) -->
      <div v-if="preset === 'custom'" class="row g-2 mt-1">
         <DateField
            :model-value="startDate"
            col="col-6"
            label="Start"
            icon="fa fa-clock-o"
            with-time
            @update:model-value="value => $emit('update:startDate', toFilterValue(value))"
         />
         <DateField
            :model-value="endDate"
            col="col-6"
            label="End"
            icon="fa fa-clock-o"
            with-time
            @update:model-value="value => $emit('update:endDate', toFilterValue(value))"
         />
      </div>

      <!-- The exact range that will be used -->
      <small class="sa-core-color opacity-75 d-block mt-1">
         <template v-if="startDate || endDate">Shows: {{ showDate(startDate) }} → {{ showDate(endDate) }}</template>
         <template v-else>Shows: all dates</template>
      </small>
   </div>
</template>

<script>
// Date range picker for the Filter window.
// The dates are the user's local time ("YYYY-MM-DDTHH:MM").
// The axios interceptor in the app file changes them to UTC before they are sent.
import SelectField from "./SelectField.vue";
import DateField from "./DateField.vue";
import { DATE_PRESET_GROUPS } from "./datePresets.js";

export default {
   name: "DateRangeFilter",
   components: { SelectField, DateField },
   props: {
      preset: { type: String, default: "all" },
      year: { type: Number, default: () => new Date().getFullYear() },
      startDate: { type: String, default: "" },
      endDate: { type: String, default: "" },
   },
   emits: ["update:preset", "update:year", "update:startDate", "update:endDate"],
   computed: {
      // One flat list for v-select: a heading row, then its items. "Custom" is left out (it is pinned at the bottom)
      presetOptions() {
         const list = [];
         DATE_PRESET_GROUPS.forEach((group) => {
            const items = group.options.filter((o) => o.value !== "custom");
            if (!items.length) return;
            list.push({ heading: true, label: group.label, value: `heading-${group.label}` });
            items.forEach((o) => list.push({ label: o.label, value: o.value }));
         });
         return list;
      },
      // Name shown in the closed box, e.g. "This month" or "Custom…"
      selectedLabel() {
         for (const group of DATE_PRESET_GROUPS) {
            const option = group.options.find((o) => o.value === this.preset);
            if (option) return option.label;
         }
         return "All time";
      },
      needsYear() {
         return ["q1", "q2", "q3", "q4"].includes(this.preset);
      },
      // This year and the 9 before it
      yearOptions() {
         const thisYear = new Date().getFullYear();
         return Array.from({ length: 10 }, (_, i) => thisYear - i);
      },
   },
   methods: {
      choosePreset(value) {
         if (!value) return;
         this.$emit("update:preset", value);

         if (value === "all") {
            this.$emit("update:startDate", "");
            this.$emit("update:endDate", "");
            return;
         }
         if (value === "custom") return; // keep the dates already there, so they can be adjusted

         this.applyRange(value, this.year);
      },
      // Typing in the date list: keep a heading only if one of its items matches
      filterPresets(options, search) {
         const text = (search || "").trim().toLowerCase();
         if (!text) return options;

         const result = [];
         let heading = null;
         options.forEach((option) => {
            if (option.heading) {
               heading = option; // remember it, add it only if an item below matches
               return;
            }
            if (option.label.toLowerCase().includes(text)) {
               if (heading) {
                  result.push(heading);
                  heading = null;
               }
               result.push(option);
            }
         });
         return result;
      },
      // "Custom…" at the bottom of the list
      pickCustom() {
         this.choosePreset("custom");
         const select = this.$refs.presetSelect?.$refs.select; // the v-select inside SelectField
         if (select) {
            if (typeof select.closeSearchOptions === "function") select.closeSearchOptions();
            else select.open = false;
         }
      },
      chooseYear(value) {
         if (!value) return;
         const year = Number(value);
         this.$emit("update:year", year);
         if (this.needsYear) this.applyRange(this.preset, year);
      },
      applyRange(preset, year) {
         const range = this.getRange(preset, year);
         if (!range) return;
         this.$emit("update:startDate", this.toLocalValue(range[0], "00:00"));
         this.$emit("update:endDate", this.toLocalValue(range[1], "23:59"));
      },
      // Returns [firstDay, lastDay] for a preset
      getRange(preset, year) {
         const now = new Date();
         const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
         const y = today.getFullYear();
         const m = today.getMonth();             // 0 = January
         const q = Math.floor(m / 3);            // 0 = first quarter
         const addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
         const quarter = (yr, qi) => [new Date(yr, qi * 3, 1), new Date(yr, qi * 3 + 3, 0)];
         const weekStart = addDays(today, -((today.getDay() + 6) % 7)); // Monday of this week

         switch (preset) {
            case "today": return [today, today];
            case "yesterday": return [addDays(today, -1), addDays(today, -1)];
            case "last7": return [addDays(today, -6), today];
            case "last30": return [addDays(today, -29), today];

            case "thisWeekToDate": return [weekStart, today];
            case "thisWeek": return [weekStart, addDays(weekStart, 6)];
            case "lastWeek": return [addDays(weekStart, -7), addDays(weekStart, -1)];

            case "thisMonthToDate": return [new Date(y, m, 1), today];
            case "thisMonth": return [new Date(y, m, 1), new Date(y, m + 1, 0)];
            case "lastMonth": return [new Date(y, m - 1, 1), new Date(y, m, 0)];

            case "thisQuarterToDate": return [quarter(y, q)[0], today];
            case "thisQuarter": return quarter(y, q);
            case "lastQuarter": return q === 0 ? quarter(y - 1, 3) : quarter(y, q - 1);
            case "q1": return quarter(year, 0);
            case "q2": return quarter(year, 1);
            case "q3": return quarter(year, 2);
            case "q4": return quarter(year, 3);

            case "thisYearToDate": return [new Date(y, 0, 1), today];
            case "lastYear": return [new Date(y - 1, 0, 1), new Date(y - 1, 11, 31)];

            default: return null;
         }
      },
      // Date -> "YYYY-MM-DDTHH:MM" (same format as a datetime-local box)
      toLocalValue(date, time) {
         const pad = (n) => String(n).padStart(2, "0");
         return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${time}`;
      },
      // DateField gives "2026-10-01 14:30"; the filters use "2026-10-01T14:30" (like before)
      toFilterValue(value) {
         return value ? String(value).replace(" ", "T") : "";
      },
      // "2026-10-01T00:00" -> "01/10/2026 00:00"
      showDate(value) {
         if (!value) return "…";
         return value.replace(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}:\d{2}).*$/, "$3/$2/$1 $4");
      },
   },
};
</script>

<style scoped>
/* Heading rows (Days, Week, Month...): grey, small, not clickable */
.date-range-filter :deep(.vs__dropdown-option--disabled) {
   background: transparent !important;
   cursor: default;
   padding-top: 8px;
   padding-bottom: 2px;
}
.preset-heading {
   font-size: 0.7rem;
   font-weight: 700;
   letter-spacing: 0.05em;
   text-transform: uppercase;
   opacity: 0.6; /* grey in every theme */
}

/* Items: finger cursor */
.date-range-filter :deep(.vs__dropdown-option),
.date-range-filter :deep(.vs__selected-options),
.date-range-filter :deep(.vs__dropdown-toggle) {
   cursor: pointer;
}

/* No empty space under the list, so "Custom…" sits right at the bottom edge */
.date-range-filter :deep(.vs__dropdown-menu) {
   padding-bottom: 0;
}

/* "Custom…" stays visible at the bottom while the list scrolls behind it */
.preset-custom {
   position: sticky;
   bottom: 0;
   z-index: 2;                                   /* above the options */
   background-color: inherit !important;         /* the list's own (theme) colour, so options don't show through */
   border-top: 1px solid var(--sa-input-border, #ddd);
   box-shadow: 0 -4px 6px rgba(0, 0, 0, 0.08);   /* soft line so you can see the list goes on above */
   margin-top: 4px;
   padding: 9px 20px;
   font-weight: 600;
   color: var(--bs-primary, #0d6efd);
   cursor: pointer;
   list-style: none;
}
.preset-custom:hover,
.preset-custom.active {
   background-color: var(--bs-primary, #5897fb) !important;
   color: #fff;
}
</style>